import React, { useState, useEffect, useContext, useMemo } from 'react';
import { some } from 'lodash';
import { useWithUserAndTenant, useHasAccess } from 'cp-client-auth!sofe';
import { getTaskIdFromUrl, getSubtaskIdFromUrl } from './practitioner/primary-navbar.helper';
import { getTask } from './resources/tasks.resource';
import { asyncStacktrace, catchSyncStacktrace, catchAsyncStacktrace } from 'auto-trace';
import { forkJoin } from 'rxjs'
import { getSuccessfulTranscriptsCount, getNoticesCount } from './resources/free-trials.resource'

const NavContext = React.createContext();

const handleError = () => (
  asyncStacktrace(e => {
    // 404 means the user is unauthorized and tasks-ui is handling the redirect
    if(e.status !== 404) {
      catchSyncStacktrace(e)
    }
  })
)

const NavProvider = props => {
  const [hash, setHash] = useState(window.location.hash);
  const [user, tenant] = useWithUserAndTenant();
  const [secondaryOpen, setSecondaryOpen] = useState();
  const [openModal, setOpenModal] = useState();
  const [dynamicLoadedModals, setDynamicLoadedModals] = useState({});
  const [taskID, setTaskID] = useState();
  const [task, setTask] = useState();
  const [subtaskID, setSubTaskID] = useState();
  const [subtask, setSubtask] = useState();
  const [transcriptsLimitReached, setTranscriptsLimitReached] = useState(false);
  const [noticesLimitReached, setNoticesLimitReached] = useState(false);

  useEffect(() => {
    window.addEventListener('hashchange', updateHash);

    return () => window.removeEventListener('hashchange', updateHash);
  }, []);

  useEffect(() => {
    setTaskID(getTaskIdFromUrl(hash));
    setSubTaskID(getSubtaskIdFromUrl(hash));
  }, [hash]);

  useEffect(() => {
    if (!taskID) {
      setTask(null);
      return;
    }
    const subscription = getTask(taskID).subscribe(task => setTask(task), handleError());
    return () => {
      subscription && subscription.unsubscribe();
    };
  }, [taskID]);

  useEffect(() => {
    if (!subtaskID) {
      setSubtask(null);
      return;
    }
    const subscription = getTask(subtaskID).subscribe(subtask => setSubtask(subtask), handleError());
    return () => {
      subscription && subscription.unsubscribe();
    };
  }, [subtaskID]);

  const handleFreeTrialLimitEvent = ({ detail }) => {
    if (detail?.notices) {
      setNoticesLimitReached(true);
    }
    if (detail?.transcripts) {
      setTranscriptsLimitReached(true);
    }
  };

  const hasTranscripts = useHasAccess('transcripts');
  const hasUnlimitedTranscripts = useHasAccess('transcripts_pull_unlimited');

  useEffect(() => {
    if (hasTranscripts && !hasUnlimitedTranscripts) {
      const subscription = forkJoin([getSuccessfulTranscriptsCount(), getNoticesCount()]).subscribe(
        ([transcriptsCount, noticesCount]) => {
          const noticesLimitReached = noticesCount?.successful_count >= noticesCount?.limit;
          const transcriptsLimitReached = transcriptsCount?.successful_count >= transcriptsCount?.limit;

          if (noticesLimitReached) {
            setNoticesLimitReached(true);
          }
          if (transcriptsLimitReached) {
            setTranscriptsLimitReached(true);
          }
        },
        catchAsyncStacktrace()
      );

      window.addEventListener('cp:tn-trial-limits-reached', handleFreeTrialLimitEvent);

      return () => {
        window.removeEventListener('cp:tn-trial-limits-reached', handleFreeTrialLimitEvent);
        subscription?.unsubscribe();
      };
    }
  }, [hasTranscripts, hasUnlimitedTranscripts]);

  const updateHash = () => {
    setHash(window.location.hash);
  };

  const isActive = regexes => {
    if (!regexes) return false;
    return some(regexes, regex => {
      return new RegExp(regex).test(hash);
    });
  };

  const isSelected = id => {
    return secondaryOpen && secondaryOpen.id === id;
  };

  const clientId = useMemo(() => {
    let myRegex = /clients?\/(\d*)/g;
    let match = myRegex.exec(hash);
    return match && Number(match[1]) ? Number(match[1]) : null;
  }, [hash]);

  const loadDynamicModal = (name, modal) => {
    setDynamicLoadedModals({ ...dynamicLoadedModals, [name]: modal });
  };

  return (
    <NavContext.Provider
      value={{
        user,
        tenant,
        clientId,
        isActive,
        isSelected,
        secondaryOpen,
        setSecondaryOpen,
        openModal,
        setOpenModal,
        dynamicLoadedModals,
        loadDynamicModal,
        task,
        subtask,
        transcriptsLimitReached,
        noticesLimitReached
      }}
      {...props}
    />
  );
};

const useNav = () => useContext(NavContext);

export { NavProvider, useNav };
